/**
 * Persistence Slice - Save/Load workflow state
 * Manages workflow metadata and dirty state
 *
 * NOTE: Only display messages (welcomeMessage, errorMessage) are in metadata.
 * ALL prompts are stored in nodes via `systemMessage` field.
 */

import type { StateCreator } from 'zustand';
import type { WorkflowStore, PersistenceSlice } from './types';

export const createPersistenceSlice: StateCreator<WorkflowStore, [], [], PersistenceSlice> = (set, get) => ({
  // Initial State
  currentWorkflowId: null,
  currentWorkflowName: 'Untitled Workflow',
  currentWorkflowDescription: '',
  isDirty: false,
  isSaving: false,
  lastSavedAt: null,
  // Workflow metadata (DISPLAY MESSAGES ONLY - no prompts here)
  workflowMetadata: {
    welcomeMessage: '',
    errorMessage: ''
  },

  // Actions
  setCurrentWorkflowId: (id) => {
    set({ currentWorkflowId: id });
  },

  setCurrentWorkflowName: (name) => {
    set({ currentWorkflowName: name, isDirty: true });
  },

  setCurrentWorkflowDescription: (description) => {
    set({ currentWorkflowDescription: description, isDirty: true });
  },

  setIsDirty: (isDirty) => {
    set({ isDirty });
  },

  setIsSaving: (isSaving) => {
    set({ isSaving });
  },

  setLastSavedAt: (timestamp) => {
    set({ lastSavedAt: timestamp });
  },

  markAsDirty: () => {
    set({ isDirty: true });
  },

  markAsSaved: () => {
    set({
      isDirty: false,
      lastSavedAt: new Date().toISOString()
    });
  },

  // Metadata actions (DISPLAY MESSAGES ONLY)
  setWorkflowMetadata: (metadata) => {
    const current = get().workflowMetadata;
    set({
      workflowMetadata: { ...current, ...metadata },
      isDirty: true
    });
  },

  setWelcomeMessage: (message) => {
    const current = get().workflowMetadata;
    set({
      workflowMetadata: { ...current, welcomeMessage: message },
      isDirty: true
    });
  },

  setErrorMessage: (message) => {
    const current = get().workflowMetadata;
    set({
      workflowMetadata: { ...current, errorMessage: message },
      isDirty: true
    });
  }
});
